import { ExclamationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import type { PracticeRiskEntry, PracticeRiskStatus } from '../../services/practiceRisk';

// Plain module (not a component) so it can export constants/functions
// alongside the .tsx components that use them without tripping
// react-refresh/only-export-components - same split as accountTypeTone.ts
// next to IdentityChip.tsx.

export const PRACTICE_RISK_STATUS_LABELS: Record<PracticeRiskStatus, string> = {
    normal: 'Normal',
    warning: 'Warning',
    at_risk: 'At risk'
};

// The one glyph type shared by every risk icon below - both Heroicons solid
// components share this exact signature, so callers can treat either as
// interchangeable without importing the icons themselves.
export type PracticeRiskIconComponent = typeof ExclamationTriangleIcon;

// Warning/at-risk glyph shown ahead of the status text everywhere a flagged
// practice is labeled (PracticeDoctorLabel's inline label, RiskStatusPill's
// header pill, and the risk-status pill selector on the Details tab) - solid
// variants read better at the small inline size these render at. No entry
// for 'normal': every consumer already renders nothing extra for that state.
export const PRACTICE_RISK_ICONS: Record<
    Exclude<PracticeRiskStatus, 'normal'>,
    PracticeRiskIconComponent
> = {
    warning: ExclamationTriangleIcon,
    at_risk: ExclamationCircleIcon
};

// Text-only treatment used inline (PracticeDoctorLabel's colored practice
// name) - deliberately not a filled pill so it doesn't compete visually with
// the row content it sits alongside. Callers pair this with the matching
// icon above (see PracticeDoctorLabel) so the signal is never color-only.
export const PRACTICE_RISK_TEXT_CLASSES: Record<Exclude<PracticeRiskStatus, 'normal'>, string> = {
    warning: 'text-yellow-600',
    at_risk: 'text-red-600'
};

// Bordered pill treatment - used by the practice detail header's status pill (see RiskStatusPill).
export const PRACTICE_RISK_PILL_CLASSES: Record<Exclude<PracticeRiskStatus, 'normal'>, string> = {
    warning: 'bg-yellow-50 text-yellow-700 border border-yellow-300',
    at_risk: 'bg-red-50 text-red-700 border border-red-300'
};

/**
 * "Warning: {note}" / "At risk: {note}" - the hover explanation shown
 * wherever a flagged practice's name/label appears. Falls back to the bare
 * status label when no note was recorded.
 */
export function practiceRiskTooltipContent(
    entry: Pick<PracticeRiskEntry, 'riskStatus' | 'riskNote'>
): string {
    const label = PRACTICE_RISK_STATUS_LABELS[entry.riskStatus];
    const note = entry.riskNote?.trim();
    return note ? `${label}: ${note}` : label;
}
